import {
    all,
    put,
    select,
    takeLatest,
    call,
    StrictEffect,
} from 'redux-saga/effects';
import {
    CHANGE_CAPACITY,
    CHANGE_COLOR,
    COLOR_KEY,
    SET_ATOMIC_ID,
    SET_DEFAULT_STATE,
} from './constants';
import {
    selectAtomicDevice,
    selectAtomicId,
    selectOptionsByAtomicId,
    selectColors,
    selectCurrentCapacity,
    selectDefaultSize,
    selectSizesForColor,
} from './selectors';
import {
    SAILS_PARAM_ATOMIC_ID,
    SAILS_BNT_STORAGE,
    URL_PARAM_ATOMIC_ID,
    SAILS_PARAM_DEVICE_ID,
    ADDITIONAL_PAGE_OPTIONS,
} from '@vfde-fe/sails-constants';
import { updateStorageSaga } from '@vfde-fe/sails-storage';
import { getAtomicId } from './helpers/getAtomicId';
import { getNewSelectedCapacity } from './helpers/capacityHelper';
import { trackPageView } from './helpers/tracking';
import {
    hasQueryParam,
    updateUrl,
    createUrlWithQueryString,
} from '@vfde-fe/utils';
import { SelectorReturnType } from '@vfde-fe/sails_core';
import { optionsActionCreators } from './actions';

/**
 * Options saga
 * Watches actions
 */
export default function* optionsSaga () {
    yield takeLatest(SET_DEFAULT_STATE, initColorCapacitySaga);
    yield takeLatest(CHANGE_COLOR, colorChangeSaga);
    yield takeLatest(CHANGE_CAPACITY, capacityChangeSaga);
    yield takeLatest(SET_ATOMIC_ID, setAtomicIdSaga);
}

/**
 * Initialize color and capacity on first load
 */
export function* initColorCapacitySaga (): Generator<StrictEffect> {
    const [colors, defaultSize, atomicId] = (yield all([
        select(selectColors()),
        select(selectDefaultSize()),
        call(getAtomicId),
    ])) as [
        SelectorReturnType<typeof selectColors>,
        SelectorReturnType<typeof selectDefaultSize>,
        ReturnType<typeof getAtomicId>,
    ];

    let initialColor: string | null |  undefined;
    let initialCapacity: number | null | undefined;

    // Set initial options from the atomicId if exist
    if (atomicId) {
        const optionsByAtomic = (yield select(selectOptionsByAtomicId(atomicId))) as SelectorReturnType<typeof selectOptionsByAtomicId>;
        initialColor = optionsByAtomic.color;
        initialCapacity = optionsByAtomic.capacity;
    }

    // If no valid atomic Id, use default values
    if (!initialColor || !initialCapacity) {
        initialColor = colors![0][COLOR_KEY];
        initialCapacity =  defaultSize;
    }

    yield all([
        put(optionsActionCreators.changeCapacity(initialCapacity!)),
        put(optionsActionCreators.changeColor(initialColor)),
    ]);
}

/**
 * Handle changing of colors
 */
export function* colorChangeSaga (): Generator<StrictEffect> {
    const [currentCapacity, atomicDevice] = (yield all([
        select(selectCurrentCapacity()),
        select(selectAtomicDevice()),
    ])) as [
        SelectorReturnType<typeof selectCurrentCapacity>,
        SelectorReturnType<typeof selectAtomicDevice>,
    ];

    if (atomicDevice) {
        yield put(optionsActionCreators.setAtomicId(atomicDevice.atomicId));
    } else {
        const availableSizes = (yield select(selectSizesForColor())) as SelectorReturnType<typeof selectSizesForColor>;

        // Handle case when a previously selected capacity is not available for the selected color
        const newCapacity = getNewSelectedCapacity(availableSizes!, currentCapacity);
        yield put(optionsActionCreators.changeCapacity(newCapacity));
    }
}

/**
 * Update atomic ID when capacity change
 */
export function* capacityChangeSaga () {
    yield call(updateAtomicId, { forceRerender: true });
}

/**
 * Update atomic ID when capacity change
 */
// tslint:disable-next-line: completed-docs
export function* updateAtomicId ({ forceRerender }: { forceRerender: boolean }): Generator<StrictEffect> {
    const atomicDevice = (yield select(selectAtomicDevice())) as SelectorReturnType<typeof selectAtomicDevice>;

    if (atomicDevice) {
        yield put(optionsActionCreators.setAtomicId(atomicDevice.atomicId, forceRerender));
    }
}

/**
 * Set atomic and device Id in session storage
 */
export function* setAtomicIdSaga (): Generator<StrictEffect> {
    const atomicId = (yield select(selectAtomicId())) as SelectorReturnType<typeof selectAtomicId>;
    const { deviceId } = window[ADDITIONAL_PAGE_OPTIONS];

    if (atomicId) {
        yield call(updateStorageSaga, SAILS_BNT_STORAGE, {
            [SAILS_PARAM_ATOMIC_ID]: atomicId,
            [SAILS_PARAM_DEVICE_ID]: deviceId,
        });

        yield call(trackPageView);

        if (hasQueryParam(URL_PARAM_ATOMIC_ID)) {
            yield call(updateUrl, createUrlWithQueryString(URL_PARAM_ATOMIC_ID, `${atomicId}`), true);
        }
    }
}
